import { ParcelSearchesSource } from '@/constants';
import { useGlobalState, useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import { PMTAdType } from '@/modules/pmt/constants';
import { useEmailSyncContext, useShipmentsOverviewContext } from '@/modules/pmt/context';
import { useFetchShipments } from '@/modules/pmt/hooks';
import { recordSearchT1 } from '@/modules/pmt/utils';
import { gtmParcelSearches, gtmViewParcel } from '@/utils';
import { useStatsigClient } from '@statsig/react-bindings';
import type { FC } from 'react';
import { CustomNoParcelsAddedAdsense } from './DefaultTrackingContainer.styles';
import { DefaultBanner } from './components/DefaultBanner';
import { ESDefaultBanner } from './components/ESDefaultBanner';
import { LoggedInConnectedESBanner } from './components/LoggedInConnectedESBanner';
import { LoggedInESBanner } from './components/LoggedInESBanner';

export const DefaultTrackingContainer: FC<{
  showEmailConnectedBanner?: boolean;
}> = ({ showEmailConnectedBanner = false }) => {
  const { isLoggedIn } = useGlobalState();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const { hasEmailAccounts, hasEmailConnected, isEmailSyncAllowed } = useEmailSyncContext();
  const { setShouldAutoOpenMobileDetails } = useShipmentsOverviewContext();
  const { fetchShipments } = useFetchShipments();
  const { logEvent } = useStatsigClient();

  const renderPmtSquareAdsense = () => {
    if (isDesktopPMT) return;
    return <CustomNoParcelsAddedAdsense className='pmt-square' variant={PMTAdType.PMT_SQUARE_AD} />;
  };

  const searchShipments = (trackingIds: string[]) => {
    fetchShipments(trackingIds);
    recordSearchT1(trackingIds);
    gtmParcelSearches(trackingIds.length, ParcelSearchesSource.PMT);
    logEvent('parcel_searches', trackingIds.length, { source: ParcelSearchesSource.PMT });
    gtmViewParcel(); // Desktop #2

    // Auto open mobile details view if single shipment is being tracked
    // Ticket: https://parcelperform.atlassian.net/browse/PHOENIX-1163
    const isSingleShipment = trackingIds.length === 1;
    if (isSingleShipment && !isDesktopPMT) {
      setShouldAutoOpenMobileDetails(true);
    }
  };

  const commonProps = {
    squareAd: renderPmtSquareAdsense(),
    searchShipments,
  };

  if (isLoggedIn) {
    if (!isEmailSyncAllowed)
      return <DefaultBanner {...commonProps} showEmailConnectedBanner={showEmailConnectedBanner} />;
    if (!hasEmailAccounts) return <LoggedInESBanner {...commonProps} />;
    if (!hasEmailConnected) return <LoggedInESBanner {...commonProps} />;
    return <LoggedInConnectedESBanner {...commonProps} />;
  }

  if (isEmailSyncAllowed) return <ESDefaultBanner {...commonProps} />;
  return <DefaultBanner {...commonProps} showEmailConnectedBanner={showEmailConnectedBanner} />;
};
